<div class="flex flex-col space-y-2 md:p-4">
  <div class="grid grid-cols-[10%_30%_60%] md:grid-cols-[10%_50%_40%] mr-0.5 md:mr-0 overflow-hidden">
    <a routerLink="/energy-admin">
      <div class="pt-4.0 h-8 w-8" id="performance-back-arrow"></div>
    </a>
    <div>
      <h1 class="pt-2.5">Energy Balance Debug</h1>
    </div>

    <div class="flex justify-center items-center bg-gray-100 rounded-md mr-2 md:mr-0 w-auto overflow-hidden">
      <app-day-datepicker [date]="selectedDate()" (dateChange)="updateDate($event)"></app-day-datepicker>
    </div>
  </div>

  @if (isLoading()) {
    <div class="px-2 text-sm text-gray-600">Lade Daily Debug Balance ...</div>
  }

  <div class="pb-5">
    @if (tables().length === 0) {
      <div class="text-sm text-gray-500 px-2">Keine Daily-Debug-Balance-Daten.</div>
    } @else {
      <div class="flex space-x-3 overflow-x-auto pb-2">
        @for (table of tables(); track table.id + '-' + $index; let idx = $index) {
          <div class="border border-gray-200 rounded-md bg-white flex-none min-w-[30rem]">
            <div class="px-3 py-2 border-b border-gray-200 bg-gray-100 text-sm">
              <div class="font-semibold">{{ table.name || ('Node ' + (idx + 1)) }}</div>
              <div class="text-xs text-gray-500">{{ table.id }}</div>
            </div>
            <div class="max-h-[70vh] overflow-y-auto">
              <table class="w-full text-xs">
                <thead class="sticky top-0 bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th class="text-left px-2 py-1 font-semibold">timestamp</th>
                    <th class="text-right px-2 py-1 font-semibold">energyTotalIn</th>
                    <th class="text-right px-2 py-1 font-semibold">energyTotalOut</th>
                    <th class="text-right px-2 py-1 font-semibold">energyTotalInOut</th>
                  </tr>
                </thead>
                <tbody>
                  @if (table.rows.length === 0) {
                    <tr>
                      <td colspan="4" class="px-2 py-2 text-gray-400">Keine Zeilen für diese Node.</td>
                    </tr>
                  } @else {
                    @for (row of table.rows; track (row.timestamp ?? '') + '-' + $index) {
                      <tr class="border-b border-gray-100">
                        <td class="px-2 py-1 align-top font-mono whitespace-nowrap">{{ formatTimestamp(row.timestamp) }}</td>
                        <td class="px-2 py-1 align-top text-right font-mono">{{ formatValue(row.energyTotalIn) }}</td>
                        <td class="px-2 py-1 align-top text-right font-mono">{{ formatValue(row.energyTotalOut) }}</td>
                        <td class="px-2 py-1 align-top text-right font-mono">{{ formatValue(row.energyTotalInOut) }}</td>
                      </tr>
                    }
                  }
                </tbody>
                <tfoot class="border-t-2 border-gray-300 bg-gray-50">
                  <tr class="border-t border-gray-200">
                    <td class="px-2 py-1 font-medium">delta (last-first)</td>
                    <td class="px-2 py-1 text-right font-mono">{{ formatValue(table.summary.energyTotalIn.difference) }}</td>
                    <td class="px-2 py-1 text-right font-mono">{{ formatValue(table.summary.energyTotalOut.difference) }}</td>
                    <td class="px-2 py-1 text-right font-mono">{{ formatValue(table.summary.energyTotalInOut.difference) }}</td>
                  </tr>
                  <tr class="border-t border-gray-200">
                    <td class="px-2 py-1 font-medium">trend (up/down/mono)</td>
                    <td class="px-2 py-1 text-right font-mono">{{ formatTrendSummary(table.summary.energyTotalIn) }}</td>
                    <td class="px-2 py-1 text-right font-mono">{{ formatTrendSummary(table.summary.energyTotalOut) }}</td>
                    <td class="px-2 py-1 text-right font-mono">{{ formatTrendSummary(table.summary.energyTotalInOut) }}</td>
                  </tr>
                  @if (table.matchedFields.length > 0) {
                    @for (field of table.matchedFields; track field.key + '-' + $index) {
                      <tr class="border-t border-gray-200">
                        <td class="px-2 py-1 font-medium">{{ field.key }}</td>
                        <td class="px-2 py-1"></td>
                        <td class="px-2 py-1"></td>
                        <td class="px-2 py-1 text-right font-mono font-semibold">{{ formatValue(field.value) }}</td>
                      </tr>
                    }
                  }
                </tfoot>
              </table>
            </div>
          </div>
        }
      </div>
    }

    @if (isDebugStatusLoading()) {
      <div class="px-2 text-sm text-gray-600 mt-4">Lade Daily Debug Status ...</div>
    }

    @if (debugStatusSections().length > 0 || derivedBatteryToGridRows().length > 0) {
      <div class="mt-4 grid gap-3 xl:grid-cols-[minmax(0,1fr)_30rem] items-start">
        @if (debugStatusSections().length > 0) {
          <div class="space-y-3 min-w-0">
            @for (section of debugStatusSections(); track section.key) {
              <div class="border border-gray-200 rounded-md bg-white">
                <div class="px-3 py-2 border-b border-gray-200 bg-gray-100 text-sm">
                  <div class="font-semibold">{{ section.label }}</div>
                </div>
                <div class="p-3 overflow-x-auto">
                  <div class="flex space-x-3 min-w-max">
                    @for (table of section.tables; track table.name + '-' + $index; let idx = $index) {
                      <div class="border border-gray-200 rounded-md bg-white flex-none min-w-[14rem]">
                        <div class="px-3 py-2 border-b border-gray-200 bg-gray-100 text-sm">
                          <div class="font-semibold">{{ table.name || ('Node ' + (idx + 1)) }}</div>
                        </div>
                        <div class="max-h-[28rem] overflow-y-auto">
                          <table class="w-full text-xs">
                            <thead class="sticky top-0 bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th class="text-left px-2 py-1 font-semibold">timestamp</th>
                                <th class="text-right px-2 py-1 font-semibold">value</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (row of table.rows; track row.timestamp + '-' + $index) {
                                <tr class="border-b border-gray-100">
                                  <td class="px-2 py-1 align-top font-mono whitespace-nowrap">{{ formatTimestamp(row.timestamp) }}</td>
                                  <td class="px-2 py-1 align-top text-right font-mono">{{ formatValue(row.value) }}</td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      </div>
                    }
                  </div>
                </div>
              </div>
            }
          </div>
        }

        <div class="border border-gray-200 rounded-md bg-white">
          <div class="px-3 py-2 border-b border-gray-200 bg-gray-100 text-sm">
            <div class="font-semibold">derived_battery_to_grid</div>
          </div>
          <div class="max-h-[70vh] overflow-y-auto">
            <table class="w-full text-xs">
              <thead class="sticky top-0 bg-gray-50 border-b border-gray-200">
                <tr>
                  <th class="text-left px-2 py-1 font-semibold">read</th>
                  <th class="text-right px-2 py-1 font-semibold">battery_to_grid_kw</th>
                </tr>
              </thead>
              <tbody>
                @if (derivedBatteryToGridRows().length === 0) {
                  <tr>
                    <td colspan="2" class="px-2 py-2 text-gray-400">Keine battery_to_grid_derived-Daten.</td>
                  </tr>
                } @else {
                  @for (row of derivedBatteryToGridRows(); track (row.read ?? '') + '-' + $index) {
                    <tr class="border-b border-gray-100">
                      <td class="px-2 py-1 align-top font-mono whitespace-nowrap">{{ formatTimestamp(row.read) }}</td>
                      <td class="px-2 py-1 align-top text-right font-mono">{{ formatValue(row.battery_to_grid_kw) }}</td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    }

    @if (dailyBalanceResultPayload(); as payload) {
      <div class="border border-gray-200 rounded-md bg-white mt-4">
        <div class="px-3 py-2 border-b border-gray-200 bg-gray-100 text-sm">
          <div class="font-semibold">Daily Balance Result Payload</div>
        </div>
        <div class="p-3 overflow-x-auto">
          <pre class="text-xs font-mono whitespace-pre-wrap break-words">{{ payload | json }}</pre>
        </div>
      </div>
    }
  </div>
</div>
